import { defineStore } from "pinia";
import { computed, toRefs, reactive } from "vue";
import { parseInputNumber, phoneBeautifier } from "@chatfood/core-utils";
import type { IDomainGuest } from "~/domain/guest";
import { useDirectBackend } from "#imports";

// Define the state interface
type IState = IDomainGuest & {
  isLoaded: boolean;
  startedFlow: boolean;
};

type IPersistedSnapshot = Pick<
  IDomainGuest,
  "name" | "email" | "phoneNumber" | "countryCode"
>;

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    startedFlow: false,
    name: undefined,
    email: undefined,
    phoneNumber: undefined,
    countryCode: undefined,
    address: undefined,
  };
}

function createInitialSnapshot(): IPersistedSnapshot {
  return {
    name: undefined,
    email: undefined,
    phoneNumber: undefined,
    countryCode: undefined,
  };
}

export const useGuestStore = defineStore("guest", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Snapshot of the last personal info successfully persisted to the backend.
  let lastPersisted: IPersistedSnapshot = createInitialSnapshot();

  const isPersonalInfoElegibleToCheckout = computed(() => {
    const isNameValid = Boolean(state.name && state.name?.length > 3);

    const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isEmailValid = regexEmail.test(state?.email ?? "");

    const isPhoneNumberValid =
      parseInputNumber(
        phoneBeautifier(state.phoneNumber ?? "", state.countryCode, "e164") ??
          "",
      )?.valid ?? false;

    return isNameValid && isEmailValid && isPhoneNumberValid;
  });

  const isAddressElegibleToCheckout = computed(() => {
    return (
      state.address?.lat !== undefined &&
      state.address?.lng !== undefined &&
      state.address?.location !== undefined
    );
  });

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
    lastPersisted = createInitialSnapshot();
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const { authRepo } = useDirectBackend();

  function setStartedFlow(status: boolean) {
    state.startedFlow = status;
  }

  async function addAddress(address: IDomainGuest["address"]) {
    const objPayload = {
      name: state.name,
      email: state.email,
      phoneNumber: state.phoneNumber,
      countryCode: state.countryCode,
      address,
    };

    try {
      await authRepo.setGuest(objPayload);
      patch({ address });
    } catch {
      // do nothing
    }
  }

  async function removeAddress() {
    const objPayload = {
      name: state.name,
      email: state.email,
      phoneNumber: state.phoneNumber,
      countryCode: state.countryCode,
      address: undefined,
    };

    try {
      await authRepo.setGuest(objPayload);

      patch({ address: undefined });
    } catch {
      // do nothing
    }
  }

  async function update(guest: Partial<IDomainGuest>): Promise<boolean> {
    const objPayload = {
      name: guest.name,
      email: guest.email,
      phoneNumber: guest.phoneNumber,
      countryCode: guest.countryCode,
      address: state.address,
    };

    if (
      objPayload.name === lastPersisted.name &&
      objPayload.email === lastPersisted.email &&
      objPayload.phoneNumber === lastPersisted.phoneNumber &&
      objPayload.countryCode === lastPersisted.countryCode
    ) {
      return true;
    }

    try {
      await authRepo.setGuest(objPayload);

      patch({
        isLoaded: true,
        ...objPayload,
      });

      lastPersisted = {
        name: objPayload.name,
        email: objPayload.email,
        phoneNumber: objPayload.phoneNumber,
        countryCode: objPayload.countryCode,
      };

      return true;
    } catch {
      return false;
    }
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    setStartedFlow,
    addAddress,
    removeAddress,
    update,
    isPersonalInfoElegibleToCheckout,
    isAddressElegibleToCheckout,
  };
});
